export type FeedbackRecipient = {
  email: string;
  name?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidFeedbackEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim().toLowerCase());
}

export function normalizeFeedbackEmail(email: string): string {
  return email.trim().toLowerCase();
}

function splitCsvRow(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      const next = line[i + 1];
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if ((char === ',' || char === ';') && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
}

function parseAngleRecipient(value: string): FeedbackRecipient | null {
  const angle = value.trim().match(/^(.*?)\s*<([^>]+)>\s*$/);
  if (!angle) return null;
  return {
    name: angle[1]?.trim() || undefined,
    email: normalizeFeedbackEmail(angle[2] ?? ''),
  };
}

function cellLooksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  if (parseAngleRecipient(trimmed)) return true;
  return trimmed.includes('@');
}

/** One CSV/text line → one or more recipients, keeping names next to emails. */
export function parseFeedbackRecipientLine(line: string): FeedbackRecipient[] {
  const trimmed = line.trim();
  if (!trimmed) return [];

  const asAngle = parseAngleRecipient(trimmed);
  if (asAngle) return [asAngle];

  const cells = splitCsvRow(trimmed).filter(Boolean);
  if (cells.length >= 2) {
    const complete = cells.map((cell) => {
      const angle = parseAngleRecipient(cell);
      if (angle) return angle;
      if (cellLooksLikeEmail(cell)) {
        return { email: normalizeFeedbackEmail(cell) };
      }
      return null;
    });
    if (complete.every((entry): entry is FeedbackRecipient => entry != null)) {
      return complete;
    }

    const emailIdx = cells.findIndex(
      (cell) => parseAngleRecipient(cell) != null || isValidFeedbackEmail(cell),
    );
    if (emailIdx >= 0) {
      const emailCell = cells[emailIdx] ?? '';
      const angled = parseAngleRecipient(emailCell);
      const email = angled?.email ?? normalizeFeedbackEmail(emailCell);
      const name = cells
        .filter((_, index) => index !== emailIdx)
        .join(' ')
        .trim();
      return [{ email, name: angled?.name || name || undefined }];
    }
  }

  if (trimmed.includes('@')) {
    return [{ email: normalizeFeedbackEmail(trimmed) }];
  }

  return [{ email: trimmed }];
}

/** Parse pasted or bulk text into recipient entries. */
export function parseFeedbackRecipients(raw: string): FeedbackRecipient[] {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const out: FeedbackRecipient[] = [];
  for (const line of lines) {
    out.push(...parseFeedbackRecipientLine(line));
  }
  return out.filter((entry) => entry.email.length > 0);
}

export function mergeFeedbackRecipients(
  existing: FeedbackRecipient[],
  incoming: FeedbackRecipient[],
): {
  recipients: FeedbackRecipient[];
  added: number;
  skippedInvalid: number;
  skippedDuplicate: number;
} {
  const preview = previewRecipientImport({
    existing,
    incoming,
    remainingSlots: null,
  });
  return {
    recipients: [...existing, ...preview.ready],
    added: preview.ready.length,
    skippedInvalid: preview.invalid.length,
    skippedDuplicate: preview.duplicate.length,
  };
}

const EMAIL_HEADER = new Set(['email', 'e-mail', 'mail', 'e_mail']);
const NAME_HEADER = new Set(['name', 'fullname', 'full_name', 'full-name']);

export const FEEDBACK_RECIPIENT_EXAMPLE_CSV_FILENAME =
  'feedback-recipients-example.csv';

const FEEDBACK_RECIPIENT_EXAMPLE_ROWS = [
  'name,email',
  'Anna Müller,anna.mueller@example.com',
  'Jonas Weber,jonas.weber@example.com',
  'Lea Hoffmann,lea.hoffmann@example.com',
] as const;

/** UTF-8 CSV with BOM so Excel keeps umlauts. Header matches the importer. */
export function buildFeedbackRecipientExampleCsv(): string {
  return `\uFEFF${FEEDBACK_RECIPIENT_EXAMPLE_ROWS.join('\n')}\n`;
}

function stripBom(raw: string): string {
  return raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
}

/** Parse a CSV/text file of recipients. Header row with `email` is optional. */
export function parseFeedbackRecipientCsv(raw: string): FeedbackRecipient[] {
  const lines = stripBom(raw)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const headerCells = splitCsvRow(lines[0] ?? '').map((cell) =>
    cell.trim().toLowerCase(),
  );
  const emailIdx = headerCells.findIndex((cell) => EMAIL_HEADER.has(cell));
  if (emailIdx < 0) {
    return parseFeedbackRecipients(raw);
  }

  const nameIdx = headerCells.findIndex((cell) => NAME_HEADER.has(cell));
  const out: FeedbackRecipient[] = [];
  for (const line of lines.slice(1)) {
    const cells = splitCsvRow(line);
    const email = normalizeFeedbackEmail(cells[emailIdx] ?? '');
    if (!email) continue;
    const name =
      nameIdx >= 0 ? cells[nameIdx]?.replace(/^"|"$/g, '').trim() : undefined;
    out.push({ email, name: name || undefined });
  }
  return out;
}

export type RecipientImportPreview = {
  ready: FeedbackRecipient[];
  invalid: FeedbackRecipient[];
  duplicate: FeedbackRecipient[];
  overQuota: FeedbackRecipient[];
};

/**
 * Classify incoming rows against the current list and remaining quota.
 * `remainingSlots` null means unlimited.
 */
export function previewRecipientImport(input: {
  existing: FeedbackRecipient[];
  incoming: FeedbackRecipient[];
  remainingSlots: number | null;
}): RecipientImportPreview {
  const seen = new Set(
    input.existing.map((entry) => normalizeFeedbackEmail(entry.email)),
  );
  const ready: FeedbackRecipient[] = [];
  const invalid: FeedbackRecipient[] = [];
  const duplicate: FeedbackRecipient[] = [];
  const overQuota: FeedbackRecipient[] = [];
  let slotsLeft = input.remainingSlots;

  for (const entry of input.incoming) {
    const email = normalizeFeedbackEmail(entry.email);
    const next: FeedbackRecipient = {
      email,
      name: entry.name?.trim() || undefined,
    };
    if (!email || !isValidFeedbackEmail(email)) {
      invalid.push(next.email ? next : { ...next, email: entry.email });
      continue;
    }
    if (seen.has(email)) {
      duplicate.push(next);
      continue;
    }
    if (slotsLeft != null && slotsLeft <= 0) {
      overQuota.push(next);
      continue;
    }
    seen.add(email);
    ready.push(next);
    if (slotsLeft != null) {
      slotsLeft -= 1;
    }
  }

  return { ready, invalid, duplicate, overQuota };
}

export function recipientImportNeedsReview(
  preview: RecipientImportPreview,
  incomingCount: number,
  options?: { skipCleanReview?: boolean },
): boolean {
  if (
    preview.invalid.length > 0 ||
    preview.duplicate.length > 0 ||
    preview.overQuota.length > 0
  ) {
    return true;
  }
  if (options?.skipCleanReview) return false;
  return incomingCount > 1;
}
